// Breakdown of the time entries behind a Spent Hours figure on the
// Budget tab — one employee's entries, or (from the Total Services row) a
// per-employee summary that drills into each employee's entries.
import React, { useEffect, useMemo, useState } from "react";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { notApplicable } from "../../Misc/Table/TanStackTableConstants";
import { fmtHours } from "./BudgetEditorTab";

const fmtEntryDate = (value) => {
	if (!value) return notApplicable;
	const date = new Date(`${String(value instanceof Date ? value.toISOString() : value).slice(0, 10)}T12:00:00`);
	if (Number.isNaN(date.getTime())) return notApplicable;
	return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const sumHours = (entries) => entries.reduce((s, e) => s + (Number(e.hours) || 0), 0);

// `timeEntries` is the phase's raw time entry list. All of it counts toward
// Spent Hours (as in BQE), with non-billable entries labelled. `initialKey` is a resourceKey
// (from `keyFor`) to open straight to one employee, or null for everyone.
// `line` ({ label, entries }) narrows that employee's view to the entries
// attributed to one budget line.
const SpentHoursDialog = ({ open, onClose, timeEntries, keyFor, initialKey, line }) => {
	const [selectedKey, setSelectedKey] = useState(initialKey);
	useEffect(() => { if (open) setSelectedKey(initialKey); }, [open, initialKey]);

	const byEmployee = useMemo(() => {
		const groups = new Map();
		(timeEntries || []).forEach((e) => {
			const key = keyFor(e.employee, e.resource_id);
			if (!key) return;
			if (!groups.has(key)) groups.set(key, { key, name: e.employee || key, entries: [] });
			groups.get(key).entries.push(e);
		});
		groups.forEach((g) => g.entries.sort((a, b) => String(b.date).localeCompare(String(a.date))));
		return [...groups.values()].map((g) => ({ ...g, hours: sumHours(g.entries) })).sort((a, b) => b.hours - a.hours);
	}, [timeEntries, keyFor]);

	const employee = selectedKey ? byEmployee.find((g) => g.key === selectedKey) : null;
	const selected = employee && line
		? {
			...employee,
			name: line.label ? `${employee.name} (${line.label})` : employee.name,
			entries: [...line.entries].sort((a, b) => String(b.date).localeCompare(String(a.date))),
			hours: sumHours(line.entries),
		}
		: employee;
	const cameFromAll = initialKey == null;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle className="spentHoursDialogTitle">
				{selected ? `Spent Hours: ${selected.name}` : "Spent Hours by Employee"}
			</DialogTitle>
			<DialogContent className="spentHoursDialogContent">
				{selected ? (
					<>
						{cameFromAll && (
							<div>
								<Button size="small" startIcon={<ArrowBackIcon />} onClick={() => setSelectedKey(null)}>All Employees</Button>
							</div>
						)}
						<div>
							<div className="spentHoursGridEntries projectViewerBudgetHeaderRow">
								<div>Date</div>
								<div>Activity</div>
								<div>Hours</div>
								<div>Memo</div>
							</div>
							{selected.entries.map((e, idx) => (
								<div key={e.id || idx} className="spentHoursGridEntries projectViewerBudgetRow">
									<div>{fmtEntryDate(e.date)}</div>
									<div className="projectViewerBudgetMuted">{e.activity_description || notApplicable}{!e.billable && " (non-billable)"}</div>
									<div>{fmtHours(e.hours)}</div>
									<div className="projectViewerBudgetMuted">{e.memo || notApplicable}</div>
								</div>
							))}
							<div className="spentHoursGridEntries projectViewerBudgetTotalsRow">
								<div style={{ fontWeight: 700 }}>Total</div>
								<div className="projectViewerBudgetMuted">{selected.entries.length} {selected.entries.length === 1 ? "entry" : "entries"}</div>
								<div style={{ fontWeight: 700 }}>{fmtHours(selected.hours)}</div>
								<div />
							</div>
						</div>
					</>
				) : byEmployee.length === 0 ? (
					<div className="projectViewerEmptyCell" style={{ padding: "22px 0" }}>No time logged on this phase yet.</div>
				) : (
					<div>
						<div className="spentHoursGridEmployees projectViewerBudgetHeaderRow">
							<div>Employee</div>
							<div>Entries</div>
							<div>Hours</div>
						</div>
						{byEmployee.map((g) => (
							<div key={g.key} className="spentHoursGridEmployees projectViewerBudgetRow">
								<div>
									<button type="button" className="spentHoursLinkButton" onClick={() => setSelectedKey(g.key)}>{g.name}</button>
								</div>
								<div className="projectViewerBudgetMuted">{g.entries.length}</div>
								<div>{fmtHours(g.hours)}</div>
							</div>
						))}
						<div className="spentHoursGridEmployees projectViewerBudgetTotalsRow">
							<div style={{ fontWeight: 700 }}>Total</div>
							<div className="projectViewerBudgetMuted">{byEmployee.reduce((s, g) => s + g.entries.length, 0)}</div>
							<div style={{ fontWeight: 700 }}>{fmtHours(byEmployee.reduce((s, g) => s + g.hours, 0))}</div>
						</div>
					</div>
				)}
				<div className="dialogButtons">
					<Button variant="contained" disableElevation onClick={onClose}>Close</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default SpentHoursDialog;
